import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Typography,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { CustomerAutocomplete } from '../../../components/forms/fields/CustomerAutocomplete'
import { SelectController } from '../../../components/forms/fields/SelectController'
import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { SkierFieldArray } from '../../../components/forms/SkierFieldArray'
import { toast } from '../../../components/LsToast'
import { createLessonRequest, updateLessonRequest } from '../../../service/lesson-requests.service'
import {
  Discipline,
  EMPTY_SKIER,
  LessonRequestDto,
  LessonType,
  RequestStatus,
  SkierLevel,
} from '../../../service/types'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'
import { RequestFormValues, useSchema } from '../schema'

const statusColorMap: Record<RequestStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  [RequestStatus.New]: 'info',
  [RequestStatus.InProgress]: 'warning',
  [RequestStatus.Done]: 'success',
  [RequestStatus.Rejected]: 'error',
}

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  onConvertToLesson?: (request: LessonRequestDto) => void
  request?: LessonRequestDto | null
}

const TIME_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const hour = (8 + i).toString().padStart(2, '0')
  return `${hour}:00`
})

export const RequestDialog: FC<Props> = ({ open, onClose, onSaved, onConvertToLesson, request }) => {
  const { t } = useTranslation()
  const schema = useSchema()

  const { handleSubmit, control, reset, setValue } = useForm<RequestFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      lessonType: LessonType.Private,
      discipline: Discipline.Ski,
      level: SkierLevel.CompleteBeginner,
      date: '',
      time: '09:00',
      customerId: '',
      customerName: '',
      notes: '',
      skiers: [EMPTY_SKIER],
      status: RequestStatus.New,
    },
  })

  useEffect(() => {
    if (request) {
      reset({
        lessonType: request.lessonType,
        discipline: request.discipline,
        level: request.level,
        date: request.date,
        time: request.time,
        customerId: request.customerId,
        customerName: request.customer?.name ?? '',
        notes: request.notes ?? '',
        skiers: request.skiers.length > 0 ? request.skiers : [{ name: '', age: 0 }],
        status: request.status,
      })
    } else {
      reset({
        lessonType: LessonType.Private,
        discipline: Discipline.Ski,
        level: SkierLevel.CompleteBeginner,
        date: '',
        time: '09:00',
        customerId: '',
        customerName: '',
        notes: '',
        skiers: [EMPTY_SKIER],
        status: RequestStatus.New,
      })
    }
  }, [request, open, reset])

  const onSubmit = async (data: RequestFormValues) => {
    try {
      const payload = {
        lessonType: data.lessonType as LessonType,
        discipline: data.discipline as Discipline,
        level: data.level as SkierLevel,
        date: data.date,
        time: data.time,
        customerId: data.customerId,
        notes: data.notes || undefined,
        skiers: (data.skiers ?? []).map((s) => ({ name: s.name!, age: s.age! })),
      }

      if (request) {
        await updateLessonRequest(request.id, payload)
      } else {
        await createLessonRequest(payload)
      }
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const handleStatusChange = async (newStatus: RequestStatus) => {
    if (!request) return
    try {
      await updateLessonRequest(request.id, { status: newStatus })
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const handleCreateLesson = () => {
    if (!request || !onConvertToLesson) return
    onConvertToLesson(request)
    onClose()
  }

  const isResolved = request && (request.status === RequestStatus.Done || request.status === RequestStatus.Rejected)

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') onClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {request ? t('requests.editRequest') : t('requests.addRequest')}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            {request && (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Chip
                    label={t(`requestStatus.${request.status}`)}
                    color={statusColorMap[request.status]}
                    size="small"
                  />
                  {!isResolved && (
                    <Box display="flex" gap={1}>
                      {request.status === RequestStatus.New && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleStatusChange(RequestStatus.InProgress)}
                        >
                          {t('requests.action.startWork')}
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleStatusChange(RequestStatus.Rejected)}
                      >
                        {t('requests.action.reject')}
                      </Button>
                      <Button size="small" variant="contained" color="primary" onClick={handleCreateLesson}>
                        {t('requests.action.createLesson')}
                      </Button>
                    </Box>
                  )}
                </Box>
                <Divider />
              </>
            )}

            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="caption">{t('requests.form.lessonType')}</Typography>
                <SelectController
                  name="lessonType"
                  control={control}
                  options={[
                    <MenuItem key="private" value="private">
                      {t('lessonType.private')}
                    </MenuItem>,
                    <MenuItem key="group" value="group">
                      {t('lessonType.group')}
                    </MenuItem>,
                  ]}
                />
              </Box>
              <Box flex={1}>
                <Typography variant="caption">{t('requests.form.discipline')}</Typography>
                <SelectController
                  name="discipline"
                  control={control}
                  options={[
                    <MenuItem key="ski" value="ski">
                      {t('discipline.ski')}
                    </MenuItem>,
                    <MenuItem key="snowboard" value="snowboard">
                      {t('discipline.snowboard')}
                    </MenuItem>,
                  ]}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption">{t('requests.form.level')}</Typography>
              <SelectController
                name="level"
                control={control}
                options={[
                  <MenuItem key="complete_beginner" value="complete_beginner">
                    {t('skierLevel.complete_beginner')}
                  </MenuItem>,
                  <MenuItem key="beginner" value="beginner">
                    {t('skierLevel.beginner')}
                  </MenuItem>,
                  <MenuItem key="intermediate" value="intermediate">
                    {t('skierLevel.intermediate')}
                  </MenuItem>,
                  <MenuItem key="upper_intermediate" value="upper_intermediate">
                    {t('skierLevel.upper_intermediate')}
                  </MenuItem>,
                ]}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="caption">{t('requests.form.date')}</Typography>
                <TextFieldController name="date" control={control} type="date" />
              </Box>
              <Box flex={1}>
                <Typography variant="caption">{t('requests.form.time')}</Typography>
                <SelectController
                  name="time"
                  control={control}
                  options={TIME_OPTIONS.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption">{t('requests.form.customer')}</Typography>
              <CustomerAutocomplete
                control={control}
                setValue={setValue}
                customerIdField="customerId"
                clientNameField="customerName"
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {t('requests.form.skiers')}
              </Typography>
              <SkierFieldArray control={control} name="skiers" />
            </Box>

            <Box>
              <Typography variant="caption">{t('requests.form.notes')}</Typography>
              <TextFieldController name="notes" control={control} multiline />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('general.button.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
